import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Command, Languages, Store } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import type { FlatNavItem } from '@/components/layout/nav.config';
import { useSearchableNavigation } from '@/components/layout/use-navigation';
import { Card, CardContent } from '@/components/ui/card';
import { useAuthStore } from '@/features/auth/auth.store';
import { useFrequentlyVisited } from '@/features/page-visits/page-visits';

/**
 * Where a session starts. Not a dashboard: the numbers live one click away,
 * and a manager who signs in to do one thing should not have to scroll past
 * a month of sales to reach it. A greeting, the store in play, and the pages
 * this account actually uses — learned from where they go, with a curated
 * list underneath for a first visit or a page they have not found yet.
 */

/**
 * The curated shortcuts, in the order they are offered. Filtered against what
 * the account may see, so a store manager never gets a Global dashboard card
 * and nobody gets a card for a page that is not built yet.
 */
const SHORTCUT_KEYS = [
  'global-dashboard',
  'store-dashboard',
  'orders-global',
  'users-global',
  'manage-bookings',
  'booking-events',
  'booking-overview',
  'item-master',
  'staff-list',
  'pos-lanes',
  'business-info',
];

const FREQUENT_LIMIT = 6;
/**
 * Asked for wider than shown. Keys come back ranked but are re-checked against
 * what this account can reach, so a role change or a store scope can drop
 * several of them — slicing before that filter is how the grid ends up short.
 */
const FREQUENT_FETCH_LIMIT = 12;

function ShortcutGrid({ items }: { items: FlatNavItem[] }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <Link
            key={item.key}
            to={item.to}
            className="bg-card hover:border-primary/50 hover:bg-accent/50 focus-visible:ring-ring/50 flex items-center gap-3 rounded-xl border p-3 transition-colors outline-none focus-visible:ring-2"
          >
            {Icon && (
              <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
                <Icon className="size-4" />
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{t(item.label)}</span>
              {item.parentLabel && (
                <span className="text-muted-foreground block truncate text-xs">
                  {t(item.parentLabel)}
                </span>
              )}
            </span>
            <ArrowRight className="text-muted-foreground/50 size-4 shrink-0" />
          </Link>
        );
      })}
    </div>
  );
}

function SectionHeading({ title, help }: { title: string; help?: string }) {
  return (
    <div>
      <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
        {title}
      </h3>
      {help && <p className="text-muted-foreground text-xs">{help}</p>}
    </div>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const profile = useAuthStore((s) => s.profile);
  const business = useAuthStore((s) => s.business);
  const reachable = useSearchableNavigation();
  const frequentKeys = useFrequentlyVisited(FREQUENT_FETCH_LIMIT);

  const byKey = (key: string) => reachable.find((entry) => entry.key === key);

  // Re-checked against what is reachable now: a page counted last month may
  // be store-only and the user is on Global today, or their role has changed.
  const frequent = frequentKeys
    .flatMap((key) => {
      const item = byKey(key);
      return item ? [item] : [];
    })
    .slice(0, FREQUENT_LIMIT);

  const shortcuts = SHORTCUT_KEYS.flatMap((key) => {
    if (frequent.some((item) => item.key === key)) return [];
    const item = byKey(key);
    return item ? [item] : [];
  });

  const tips = [
    {
      icon: Store,
      title: t('pages.home.tipStoreTitle'),
      body: business
        ? t('pages.home.tipStoreBodyScoped', { store: business.name })
        : t('pages.home.tipStoreBodyGlobal'),
    },
    { icon: Command, title: t('pages.home.tipSearchTitle'), body: t('pages.home.tipSearchBody') },
    { icon: Languages, title: t('pages.home.tipPrefsTitle'), body: t('pages.home.tipPrefsBody') },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('pages.home.welcome', { name: profile?.firstName ?? '' })}
        description={
          business
            ? t('pages.home.scopedTo', { store: business.name })
            : t('pages.home.global')
        }
      />

      {frequent.length > 0 && (
        <section className="space-y-2">
          <SectionHeading
            title={t('pages.home.frequent')}
            help={t('pages.home.frequentHelp')}
          />
          <ShortcutGrid items={frequent} />
        </section>
      )}

      {shortcuts.length > 0 && (
        <section className="space-y-2">
          <SectionHeading
            title={frequent.length > 0 ? t('pages.home.more') : t('pages.home.shortcuts')}
          />
          <ShortcutGrid items={shortcuts} />
        </section>
      )}

      <section className="space-y-2">
        <SectionHeading title={t('pages.home.goodToKnow')} />
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-3">
          {tips.map((tip) => (
            <Card key={tip.title} className="shadow-none">
              <CardContent className="flex gap-3 p-4">
                <tip.icon className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">{tip.title}</p>
                  <p className="text-muted-foreground text-sm">{tip.body}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
